/**
 * Decodes HTML entities in a string.
 * This is used after sanitizing HTML to convert entities like &amp; to & for display.
 * @param {string} encodedString - String potentially containing HTML entities
 * @returns {string} String with entities decoded
 */
export function decodeHtmlEntities(encodedString) {
  const textArea = document.createElement("textarea");
  textArea.innerHTML = encodedString;
  return textArea.value;
}

/**
 * Formats navigation text based on the visual override type.
 * Prepends "Back to" for backward navigation or "Go to" for forward navigation.
 * @param {Object} intl - react-intl intl object for formatting messages
 * @param {string} visualOverride - The visual override value (e.g., "Next", "Back", "Up", "Down")
 * @param {string} tooltipText - The tooltip text to prepend to
 * @returns {string} Formatted navigation text
 */
export function formatNavigationText(intl, visualOverride, tooltipText) {
  const navigationHotspots = ["Next", "Back", "Down", "Up"];
  const isNavigationHotspot = navigationHotspots.includes(visualOverride);

  if (!isNavigationHotspot || !tooltipText) {
    return tooltipText;
  }

  // If tooltip text is the same as navigation direction (case-insensitive),
  // don't prepend navigation text to avoid redundancy like "Back to back"
  if (tooltipText.toLowerCase() === visualOverride.toLowerCase()) {
    return visualOverride;
  }

  const isBackNavigation = ["Back", "Up"].includes(visualOverride);

  const navigationText = isBackNavigation
    ? intl.formatMessage({
        id: "backToView",
        defaultMessage: "Back to",
      })
    : intl.formatMessage({
        id: "goToView",
        defaultMessage: "Go to",
      });

  return `${navigationText} ${tooltipText}`;
}

/**
 * Converts superscript and subscript HTML tags to screen-reader-friendly text.
 * This ensures that content like "A<sub>e</sub>" is announced as "A subscript e"
 * and "x<sup>2</sup>" is announced as "x superscript 2" by screen readers.
 * @param {string} htmlString - String potentially containing <sup> and <sub> tags
 * @param {Object} intl - react-intl intl object for translations
 * @returns {string} String with sup/sub tags converted to readable text
 */
export function convertSupSubToReadableText(htmlString, intl) {
  if (!htmlString) return htmlString;

  const superscriptText = intl
    ? intl.formatMessage({
        id: "superscript",
        defaultMessage: " superscript ",
      })
    : " superscript ";

  const subscriptText = intl
    ? intl.formatMessage({
        id: "subscript",
        defaultMessage: " subscript ",
      })
    : " subscript ";

  let result = htmlString;

  result = result.replace(/<sup[^>]*>(.*?)<\/sup>/gi, `${superscriptText}$1`);
  result = result.replace(/<sub[^>]*>(.*?)<\/sub>/gi, `${subscriptText}$1`);

  return result;
}

/**
 * Converts an authored HTML instruction string into plain text suitable for a
 * screen-reader live region. Semantic sup/sub tags become spoken words
 * ("superscript"/"subscript"); every other tag is removed (replaced with a
 * space so block boundaries like </p><p> don't fuse adjacent words); HTML
 * entities are decoded; runs of whitespace are collapsed. Without this, a live
 * region reads raw markup aloud (e.g. "less-than p greater-than ... strong").
 * Entities are decoded BEFORE tags are stripped: authored simData routinely
 * escapes markup (`&lt;b&gt;`), and decoding afterwards would turn it back into
 * literal angle brackets that a screen reader reads as "less-than b
 * greater-than" — the exact defect this exists to prevent.
 *
 * The tag pattern requires a letter (or `/`) after `<` so an unescaped
 * comparison in science copy ("pH < 7 is acidic") is not swallowed as a tag.
 *
 * @param {string} htmlString - Instruction text that may contain HTML
 * @param {Object} [intl] - react-intl intl object for sup/sub translations
 * @returns {string} Plain, screen-reader-friendly text ("" for a non-string)
 */
export function htmlToScreenReaderText(htmlString, intl) {
  if (typeof htmlString !== "string") return "";

  let result = convertSupSubToReadableText(htmlString, intl);
  result = decodeHtmlEntities(result);
  result = result.replace(/<\/?[a-zA-Z][^>]*>/g, " ");
  return result.replace(/\s+/g, " ").trim();
}

/**
 * Returns react-intl formatters that convert sup/sub tags to screen-reader-friendly text.
 * This handles both HTML tags (via convertSupSubToReadableText) and ICU message format
 * tags (via these formatters).
 *
 * Example usage with intl.formatMessage():
 * - HTML format: "<sup>2</sup>" → " superscript 2"
 * - ICU format: "{sup}2{/sup}" → " superscript 2"
 *
 * @param {Object} intl - react-intl intl object for translations
 * @returns {Object} Formatters object for use with intl.formatMessage()
 */
export function getScreenReaderFormatters(intl) {
  const superscriptText = intl
    ? intl.formatMessage({
        id: "superscript",
        defaultMessage: " superscript ",
      })
    : " superscript ";

  const subscriptText = intl
    ? intl.formatMessage({
        id: "subscript",
        defaultMessage: " subscript ",
      })
    : " subscript ";

  return {
    sup: (chunks) =>
      `${superscriptText}${Array.isArray(chunks) ? chunks.join("") : chunks}`,
    sub: (chunks) =>
      `${subscriptText}${Array.isArray(chunks) ? chunks.join("") : chunks}`,
  };
}
